import { HttpParams } from "@angular/common/http";
import { format } from "date-fns";

import { BaseStandardQuote } from "../../models/quote-base-standard.model";
import { BaseLclQuote } from "../../models/quote-base-lcl.model";
import { FclQuote } from "../../models/quote-fcl.model";
import { LclQuote } from "../../models/quote-lcl.model";
import { AirQuote } from "../../models/quote-air.model";

export interface PartnerRatePackage {
  length: number;
  width: number;
  height: number;
  weight: number;
  measurement: number;
}

// Everything the partner prices a rate by. It is sent with the request, keys
// the lane cache and is stored on a selected partner rate, so a quote asking
// for anything else can tell that rate no longer applies.
export class PartnerRateCriteria {
  private constructor(
    public readonly discriminator: string,
    public readonly validFrom: string,
    public readonly incoterm: string,
    public readonly equipment: string[] = null,
    public readonly weight: number = null,
    public readonly measurement: number = null,
    public readonly pieces: number = null,
    public readonly packages: PartnerRatePackage[] = null,
  ) {}

  // Null for a quote the partner network does not serve.
  public static fromQuote(quote: BaseStandardQuote): PartnerRateCriteria {
    const validFrom = quote.getValidFrom()
      ? format(quote.getValidFrom(), "yyyy-MM-dd")
      : null;

    if (quote instanceof FclQuote) {
      return new PartnerRateCriteria(
        quote.getQuoteType(),
        validFrom,
        quote.getIncotermType() ?? null,
        [...(quote.getEquipment() ?? [])],
      );
    }

    if (quote instanceof LclQuote || quote instanceof AirQuote) {
      return PartnerRateCriteria.forCargo(quote, validFrom);
    }

    return null;
  }

  private static forCargo(
    quote: BaseLclQuote,
    validFrom: string,
  ): PartnerRateCriteria {
    return new PartnerRateCriteria(
      quote.getQuoteType(),
      validFrom,
      quote.getIncotermType() ?? null,
      null,
      quote.getWeight() || 0,
      quote.getMeasurement() || 0,
      quote.getPieces() || 0,
      (quote.getPackages() ?? []).map((item) => ({
        length: item.length ?? null,
        width: item.width ?? null,
        height: item.height ?? null,
        weight: item.weight ?? null,
        measurement: item.measurement ?? null,
      })),
    );
  }

  public toHttpParams(): HttpParams {
    let params = new HttpParams();
    const append = (key: string, value: string | number): void => {
      if (value !== null && value !== undefined) {
        params = params.append(key, value);
      }
    };

    append("criteria[discriminator]", this.discriminator);
    append("criteria[validFrom]", this.validFrom);
    append("criteria[incoterm]", this.incoterm);
    (this.equipment ?? []).forEach((container) =>
      append("criteria[equipment][]", container),
    );
    append("criteria[weight]", this.weight);
    append("criteria[measurement]", this.measurement);
    append("criteria[pieces]", this.pieces);
    (this.packages ?? []).forEach((item, index) =>
      Object.entries(item).forEach(([field, value]) =>
        append(`criteria[packages][${index}][${field}]`, value),
      ),
    );

    return params;
  }
}
